import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      category: "events",
      name: "Sarah & Michael Chen",
      role: "Wedding Couple",
      company: "Married June 2024",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      content: `Skycrest captured our wedding day with such artistry and emotion. Every time we watch our film, we're transported back to those magical moments. The team was invisible during the ceremony but captured everything perfectly.`,
      rating: 5,
      project: "Cinematic Love Story Package",
      result: "200+ family members worldwide could experience our day"
    },
    {
      id: 2,
      category: "realestate",
      name: "Jennifer Martinez",
      role: "Luxury Real Estate Agent",
      company: "Prestige Properties Group",
      avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face",
      content: `The property videos from Skycrest have revolutionized my listings. Properties sell 40% faster and at higher prices. Their cinematic approach makes every home feel like a luxury destination.`,
      rating: 5,
      project: "Premium Listing Package",
      result: "40% faster sales, 15% higher closing prices"
    },
    {
      id: 3,
      category: "marketing",
      name: "David Rodriguez",
      role: "Marketing Director",
      company: "TechFlow Solutions",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      content: `Our brand campaign with Skycrest generated 300% more engagement than previous efforts. They understood our vision and created content that truly resonates with our target audience.`,
      rating: 5,
      project: "Campaign Creator Package",
      result: "300% increase in engagement, 150% boost in conversions"
    },
    {
      id: 4,
      category: "design",
      name: "Dr. Elena Vasquez",
      role: "Innovation Director",
      company: "Future Labs Institute",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      content: `Skycrest brought our complex research concepts to life in ways we never imagined. The visualization helped us secure $2M in funding by making our vision tangible and compelling.`,
      rating: 5,
      project: "Innovation Showcase Package",
      result: "$2M funding secured, 5 strategic partnerships formed"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  const currentTestimonial = testimonials?.[activeTestimonial];

  const categoryColors = {
    events: 'text-rose-600 bg-rose-50',
    realestate: 'text-slate-600 bg-slate-50',
    marketing: 'text-orange-600 bg-orange-50',
    design: 'text-emerald-600 bg-emerald-50'
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent-foreground px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="MessageSquare" size={16} />
            <span>Client Success Stories</span>
          </div>
          
          <h2 className="text-fluid-4xl font-display font-bold text-primary mb-6">
            Trusted by Industry
            <span className="text-accent"> Leaders</span>
          </h2>
          
          <p className="text-fluid-lg text-muted-foreground leading-relaxed">
            See how we've helped clients across different industries achieve their vision and exceed their goals.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Main Testimonial */}
          <div className="bg-card border border-border rounded-2xl p-8 md:p-12 shadow-lg mb-8">
            <div className="flex flex-col md:flex-row items-start gap-8">
              {/* Avatar and Info */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <Image
                    src={currentTestimonial?.avatar}
                    alt={currentTestimonial?.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                  <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center ${
                    categoryColors?.[currentTestimonial?.category]
                  }`}>
                    <Icon 
                      name={currentTestimonial?.category === 'events' ? 'Heart' :
                           currentTestimonial?.category === 'realestate' ? 'Building' :
                           currentTestimonial?.category === 'marketing' ? 'Megaphone' : 'Zap'} 
                      size={16} 
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1">
                {/* Rating */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(currentTestimonial?.rating)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={16} className="text-yellow-400 fill-current" />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-lg text-foreground mb-6 leading-relaxed italic">
                  "{currentTestimonial?.content}"
                </blockquote>

                {/* Client Info */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-primary text-lg">
                      {currentTestimonial?.name}
                    </h4>
                    <p className="text-muted-foreground">
                      {currentTestimonial?.role}
                    </p>
                    <p className="text-sm text-accent font-medium">
                      {currentTestimonial?.company}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-sm text-muted-foreground mb-1">
                      {currentTestimonial?.project}
                    </p>
                    <p className="text-sm font-semibold text-accent">
                      {currentTestimonial?.result}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial Navigation */}
          <div className="flex items-center justify-center gap-4">
            {/* Dots */}
            <div className="flex items-center gap-2">
              {testimonials?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeTestimonial 
                      ? 'bg-accent scale-125' :'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={() => setActiveTestimonial((prev) => 
                  prev === 0 ? testimonials?.length - 1 : prev - 1
                )}
                className="w-10 h-10 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors duration-200"
              >
                <Icon name="ChevronLeft" size={18} />
              </button>
              <button
                onClick={() => setActiveTestimonial((prev) => 
                  (prev + 1) % testimonials?.length
                )}
                className="w-10 h-10 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors duration-200"
              >
                <Icon name="ChevronRight" size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 max-w-4xl mx-auto">
          {[
            { number: "500+", label: "Projects Completed" },
            { number: "98%", label: "Client Satisfaction" },
            { number: "50+", label: "Industry Awards" },
            { number: "24/7", label: "Support Available" }
          ]?.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl font-bold text-accent mb-2">
                {stat?.number}
              </div>
              <div className="text-sm text-muted-foreground">
                {stat?.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;